import { useEffect, useMemo, useRef, useState } from 'react';
import './styles/flow.css';
import config from './config/assessment.json';
import { api, setToken, getToken } from './api.js';
import { buildScreens, resumeIndex as computeResumeIndex } from './engine/buildScreens.js';
import { isVisible } from './engine/visibility.js';
import { computeProgress } from './engine/progress.js';
import { urlPrefill } from './engine/prefill.js';
import { draftView, draftsToCommit, needsPurchaseConfirm } from './engine/embed.js';
import { useAutosave } from './hooks/useAutosave.js';
import { useResume } from './hooks/useResume.js';
import QuestionRenderer from './questions/QuestionRenderer.jsx';
import Scale from './questions/Scale.jsx';
import { BackContext } from './questions/common.jsx';
import { Intro, Secure, Saved, SignIn, Done, ConfirmPurchase, FamilyMember } from './screens.jsx';

const ASSESS_ICONS = ['🩺', '🧠', '🔬'];

// `embed` (PompaBooking's /embed route only, NTAI-EMB-03): { drafts, accountEmail,
// onProgress(pct), onDone() }. When set, the Secure step and "Save & exit" are hidden,
// the purchase confirmation is skipped, and `drafts` pre-fill their questions without
// entering the answer store until Continue. Absent (every other route), nothing changes.
export default function AssessmentFlow({ enrolled: enrolledInit = false, embed = null }) {
  // Prefill from the Funnelish redirect (?email=&name=&lastName=&mobile=) — seeds the
  // initial answers so the name/email screens arrive pre-filled.
  const prefill = useMemo(() => urlPrefill(), []);
  const [enrolled, setEnrolled] = useState(enrolledInit);
  // Coach-granted, independent of `enrolled` (which is purchase-derived).
  const [additionalInfoEnabled, setAdditionalInfoEnabled] = useState(false);
  const screens = useMemo(
    () => buildScreens(config, { enrolled, additionalInfoEnabled, secure: !embed }),
    [enrolled, additionalInfoEnabled], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [cur, setCur] = useState(0);
  const [overlay, setOverlay] = useState(null); // null | 'saved' | 'signin' | 'confirm'
  const [resumeIndex, setResumeIndex] = useState(0);
  const [answers, setAnswers] = useState(prefill.answers);
  const [busy, setBusy] = useState(false);
  // Funnelish purchase context (from /leads): drives the confirm gate + family-member step.
  const [plan, setPlan] = useState(null);   // 'single' | 'multiple' | null
  const [role, setRole] = useState(null);   // 'member' when this person is a sponsored member
  const [memberAdded, setMemberAdded] = useState(false);

  const answersRef = useRef(answers);
  answersRef.current = answers;
  const profile = { gender: answers.gender };
  const setAnswer = (id, value) => setAnswers((a) => ({ ...a, [id]: value }));

  // Resume: load saved answers + enrolled on mount (same-tab refresh) or after an email
  // re-establishes a session (hydrateSaved). Each bumps `resumeSignal`; the effect below then
  // jumps to the first unanswered screen. `userMoved` stops auto-positioning once the user
  // navigates, so it never fights manual next()/back().
  const [resumeSignal, setResumeSignal] = useState(0);
  const userMoved = useRef(false);
  useResume(
    (saved) => { setAnswers((a) => ({ ...a, ...saved })); setResumeSignal((s) => s + 1); },
    setEnrolled,
    (m) => {
      setPlan(m.plan);
      setRole(m.role);
      setAdditionalInfoEnabled(!!m.additionalInfoEnabled);
    },
  );
  useAutosave(answers, { progress: computeProgress(screens, cur).pct });

  // Re-load saved progress once a session is (re-)established by email — how a passwordless
  // user resumes after their tab was closed (sessionStorage cleared).
  async function hydrateSaved() {
    try {
      const d = await api.get('/ntq');
      if (typeof d.enrolled !== 'undefined') setEnrolled(!!d.enrolled);
      if (typeof d.additional_info_enabled !== 'undefined')
        setAdditionalInfoEnabled(!!d.additional_info_enabled);
      setPlan(d.plan || null);
      setRole(d.role || null);
      if (d.answers && Object.keys(d.answers).length) {
        setAnswers((a) => ({ ...a, ...d.answers }));
        setResumeSignal((s) => s + 1);
      }
    } catch { /* offline */ }
  }

  // Soft purchase check on landing: if the URL carried an email and there's no session yet,
  // upsert the account (learn enrolled/plan) and resume any saved progress.
  const softChecked = useRef(false);
  useEffect(() => {
    if (softChecked.current || !prefill.email || getToken()) return;
    softChecked.current = true;
    api.post('/leads', { email: prefill.email })
      .then((d) => {
        if (d.token) setToken(d.token);
        if (d.enrolled) setEnrolled(true);
        if (d.additional_info_enabled) setAdditionalInfoEnabled(true);
        setPlan(d.plan || null);
        setRole(d.role || null);
        return hydrateSaved();
      })
      .catch(() => { /* offline / not-yet-deployed */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Jump to the first unanswered screen once resume data is in. Re-runs when `enrolled`
  // settles (screens change with the phased release) so it lands on the right screen, and
  // stops once the user navigates. Builds screens from the known `enrolled` to avoid a stale
  // reactive `screens` during the enrolled transition.
  useEffect(() => {
    if (resumeSignal === 0 || userMoved.current) return;
    const a = answersRef.current;
    const scr = buildScreens(config, { enrolled, additionalInfoEnabled, secure: !embed });
    const i = computeResumeIndex(scr, a, { gender: a.gender }, isVisible);
    if (i > 0) setCur(i);
  }, [resumeSignal, enrolled, additionalInfoEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleAt = (i) => {
    const s = screens[i];
    if (s.type !== 'question') return true;
    return isVisible(s.item, answersRef.current, { gender: answersRef.current.gender });
  };
  const go = (n) => { userMoved.current = true; setCur(Math.max(0, Math.min(screens.length - 1, n))); };
  const step = (dir) => {
    let i = cur + dir;
    while (i > 0 && i < screens.length - 1 && !visibleAt(i)) i += dir;
    go(i);
  };
  const next = () => step(1);
  const back = () => step(-1);
  const saveAndExit = async () => {
    setResumeIndex(cur);
    // Flush the latest answers before the exit screen (don't rely on the autosave debounce).
    try { if (getToken()) await api.patch('/ntq', { answers: answersRef.current }); } catch { /* keep local */ }
    setOverlay('saved');
  };

  // Jump to an assessment via the rail — the first UNANSWERED question in that section (so it
  // resumes rather than restarting), or its first question if the section is fully answered.
  const goToAssessment = (i) => {
    const a = answersRef.current;
    const inA = (sc) => sc.assessment === i && (sc.type === 'question' || sc.type === 'scale');
    const unanswered = (sc) => {
      if (sc.type === 'scale') return sc.items.some((it) => a[it.id] === undefined);
      if (!isVisible(sc.item, a, { gender: a.gender })) return false;
      const ids = sc.item.fields ? sc.item.fields.map((f) => f.id) : [sc.item.id];
      return ids.some((id) => a[id] === undefined);
    };
    let idx = screens.findIndex((sc) => inA(sc) && unanswered(sc));
    if (idx < 0) idx = screens.findIndex(inA);
    if (idx >= 0) go(idx);
  };

  // Email screen: create/find the account server-side, store the session, then advance.
  // If no purchase is found, pause on a confirm screen (soft gate) before proceeding.
  async function createAccountThenNext() {
    const email = answersRef.current.email;
    setBusy(true);
    let needsConfirm = false;
    try {
      const d = await api.post('/leads', { email });
      if (d.token) setToken(d.token);
      if (d.enrolled) setEnrolled(true);
      if (d.additional_info_enabled) setAdditionalInfoEnabled(true);
      setPlan(d.plan || null);
      setRole(d.role || null);
      // Skipped for PompaBooking-booked clients (pb_booked) and inside the PB embed.
      needsConfirm = needsPurchaseConfirm(d, { embed: !!embed });
    } catch {
      /* offline / not-yet-deployed — proceed with a local session */
    }
    setBusy(false);
    if (needsConfirm) { setOverlay('confirm'); return; }
    // Resume repositioning happens once on landing (soft-check), not here — repositioning
    // on the email-Continue would fight forward navigation (name is screen 0) and loop.
    next();
  }

  // "Multiple"-plan buyer adds a family member at the end. Returns
  // { info: {email, url, emailed} } on success, or { error } with a message the screen
  // shows — so a failed invite never leaves the buyer stuck with no feedback.
  async function addMember(memberEmail) {
    setBusy(true);
    try {
      const d = await api.post('/household/member', { member_email: memberEmail });
      return { info: { email: memberEmail, url: d.invite_url, emailed: !!d.emailed } };
    } catch (e) {
      return { error: (e && e.message) || 'We couldn’t send that invite. You can try a different email, or finish without inviting.' };
    } finally {
      setBusy(false);
    }
  }

  // Embed: Continue commits this screen's untouched drafts, then advances. The email
  // screen skips /leads when the email is the one PompaBooking signed in (the PB session
  // is already open); a different email takes the normal /leads path.
  function advanceEmbedded(item, emailScreen) {
    const patch = draftsToCommit(item, embed.drafts, answersRef.current);
    if (Object.keys(patch).length) {
      answersRef.current = { ...answersRef.current, ...patch };
      setAnswers((a) => ({ ...a, ...draftsToCommit(item, embed.drafts, a) }));
    }
    const email = String(answersRef.current.email || '').trim().toLowerCase();
    if (emailScreen && !(embed.accountEmail && email === embed.accountEmail)) {
      createAccountThenNext();
      return;
    }
    next();
  }

  // Deferred password step.
  async function savePassword({ password, mobile }) {
    try {
      await api.post('/auth/set-password', { password, mobile_phone: mobile });
    } catch { /* best-effort */ }
    next();
  }

  const s = screens[cur];
  const { segments, pct, minsLeft, curA } = computeProgress(screens, cur);
  // Only the assessments that actually produced screens. A coach-gated stage that is
  // closed yields no sections, hence no screens — so it shows no pill and no intro,
  // rather than an empty tab that a client can't do anything with.
  const assessments = useMemo(() => {
    const present = new Set(
      screens.map((sc) => sc.assessment).filter((n) => n !== undefined),
    );
    return config.assessments.filter((_a, i) => present.has(i));
  }, [screens]);

  // Track the furthest assessment reached (monotonic). Rail navigation is gated on
  // this, not the current position — so jumping back to a finished assessment never
  // re-locks the ones you'd already reached.
  const [maxA, setMaxA] = useState(0);
  useEffect(() => {
    setMaxA((m) => Math.max(m, Math.min(curA, assessments.length - 1)));
  }, [curA, assessments.length]);

  // Embed: the parent page shows the same % the flow shows (and autosave sends).
  useEffect(() => {
    if (embed && embed.onProgress) embed.onProgress(pct);
  }, [pct]); // eslint-disable-line react-hooks/exhaustive-deps

  // Finalize once, on reaching the done screen: server scores + syncs to HubSpot.
  const submitted = useRef(false);
  useEffect(() => {
    if (s.type === 'done' && !submitted.current && getToken()) {
      submitted.current = true;
      const sent = api.post('/ntq/submit', { answers: answersRef.current }).catch(() => {});
      if (embed && embed.onDone) sent.then(() => embed.onDone());
    }
  }, [s.type]); // eslint-disable-line react-hooks/exhaustive-deps

  if (overlay === 'confirm') {
    return (
      <Frame>
        <ConfirmPurchase
          email={answers.email || ''}
          busy={busy}
          onContinue={() => { setOverlay(null); next(); }}
          onEdit={() => setOverlay(null)}
        />
      </Frame>
    );
  }
  if (overlay === 'saved') {
    return (
      <Frame>
        <Saved email={answers.email || ''} onResume={() => { setOverlay(null); go(resumeIndex); }} />
      </Frame>
    );
  }
  if (overlay === 'signin') {
    return (
      <Frame>
        <SignIn email={answers.email || ''} onSignIn={async ({ email, password }) => {
          try {
            const d = await api.post('/auth/login', { email, password });
            if (d.token) setToken(d.token);
            if (d.enrolled) setEnrolled(true);
            if (d.additional_info_enabled) setAdditionalInfoEnabled(true);
          } catch { /* show inline error later */ }
          setOverlay(null);
          go(resumeIndex);
        }} />
      </Frame>
    );
  }

  const pill = s.type === 'done'
    ? { text: 'Complete', accent: false }
    : s.type === 'secure'
      ? { text: 'Save your progress', accent: true }
      : { text: assessments[curA]?.name || '', accent: true };

  return (
    <Frame>
      <div className="topbar">
        <div className="lead">
          <div className="brand">
            <div className="ppmark">P</div>
            <div className="ppwm"><span className="a">POMPA</span> <span className="b">PROGRAM</span></div>
          </div>
          <span className={`pill${pill.accent ? ' accent' : ''}`} style={{ display: 'inline-flex' }}>{pill.text}</span>
        </div>
        {!embed && s.type !== 'done' && <button className="savebtn" onClick={saveAndExit}>Save &amp; exit</button>}
      </div>

      <div className="progwrap">
        <div className="segwrap">
          {segments.map((f, i) => (
            <div className="seg" key={i}><div className="segf" style={{ width: `${Math.round(f * 100)}%` }} /></div>
          ))}
        </div>
        <div className="metawrap">
          <span className="meta">{s.type === 'done' ? '100% — complete' : `${pct}% — about ${minsLeft} min left`}</span>
        </div>
      </div>

      <div className="body">
        <div className="rail">
          {assessments.map((a, i) => {
            const done = i < curA || s.type === 'done';
            const active = i === curA && s.type !== 'done';
            const navigable = s.type !== 'done' && i <= maxA;
            return (
              <button
                type="button"
                className={`railitem${active ? ' active' : ''}${done ? ' done' : ''}${navigable ? ' nav' : ''}`}
                key={a.id}
                disabled={!navigable}
                aria-current={active ? 'step' : undefined}
                onClick={() => goToAssessment(i)}
              >
                <span className="ri">{done ? '✓' : ASSESS_ICONS[i]}</span>
                <span>{a.name}</span>
              </button>
            );
          })}
        </div>
        <div className="main">
          <div className="col">
            <div className="card">
              <BackContext.Provider value={cur > 0 && s.type !== 'done' ? back : null}>
                {renderScreen()}
              </BackContext.Provider>
            </div>
          </div>
        </div>
      </div>
    </Frame>
  );

  function renderScreen() {
    if (s.type === 'intro') {
      return <Intro assessment={assessments[s.assessment]} index={s.assessment} count={assessments.length} onBegin={next} />;
    }
    if (s.type === 'secure') return <Secure onSave={savePassword} onSkip={next} initialMobile={prefill.mobile} />;
    if (s.type === 'done') {
      // A "multiple"-plan buyer (not themselves a sponsored member) is asked for their
      // family member's email once, before the thank-you.
      if (plan === 'multiple' && role !== 'member' && !memberAdded) {
        return <FamilyMember onAdd={addMember} onSkip={() => setMemberAdded(true)} busy={busy} />;
      }
      return <Done assessments={assessments} />;
    }
    if (s.type === 'scale') {
      return <Scale key={s.sectionKey + s.page} screen={s} answers={answers} setAnswer={setAnswer} onAdvance={next} />;
    }
    const emailScreen = s.item.creates_account;
    if (embed) {
      return (
        <QuestionRenderer
          key={s.item.id}
          item={s.item}
          answers={draftView(embed.drafts, answers)}
          setAnswer={setAnswer}
          onAdvance={() => advanceEmbedded(s.item, emailScreen)}
          busy={emailScreen ? busy : undefined}
        />
      );
    }
    return (
      <QuestionRenderer
        key={s.item.id}
        item={s.item}
        answers={answers}
        setAnswer={setAnswer}
        onAdvance={emailScreen ? createAccountThenNext : next}
        busy={emailScreen ? busy : undefined}
      />
    );
  }
}

function Frame({ children }) {
  return <div className="frame">{children}</div>;
}
